import countries, { type LocaleData } from 'i18n-iso-countries';
import type { Country, TranslateFn } from '@tulasihome/shared';
import { flagEmoji } from '@tulasihome/shared';
import enLocale from 'i18n-iso-countries/langs/en.json';
import ukLocale from 'i18n-iso-countries/langs/uk.json';

type SupportedLocale = 'en' | 'uk';

const STATIC_LOCALE_DATA: Record<SupportedLocale, LocaleData> = {
  en: enLocale as unknown as LocaleData,
  uk: ukLocale as unknown as LocaleData,
};

const REGISTERED_LOCALES = new Set<SupportedLocale>();

const normalizeLocale = (raw: string | null | undefined): SupportedLocale => {
  if (!raw) {
    return 'en';
  }

  const normalized = raw.trim().toLowerCase().split(/[-_]/)[0];
  return normalized === 'uk' ? 'uk' : 'en';
};

const registerLocale = (candidate: SupportedLocale) => {
  if (!REGISTERED_LOCALES.has(candidate)) {
    countries.registerLocale(STATIC_LOCALE_DATA[candidate]);
    REGISTERED_LOCALES.add(candidate);
  }
};

const ensureLocaleRegistered = (locale: SupportedLocale): SupportedLocale => {
  registerLocale(locale);
  registerLocale('en');
  return locale;
};

export const getDatasetCountryName = (isoCode: string, locale: string): string | null => {
  if (!isoCode) {
    return null;
  }

  const normalizedLocale = ensureLocaleRegistered(normalizeLocale(locale));
  const localized = countries.getName(isoCode, normalizedLocale, { select: 'official' });
  if (localized) {
    return localized;
  }

  return countries.getName(isoCode, 'en', { select: 'official' }) ?? null;
};

export const resolveCountryLocalizedName = (
  country: Pick<Country, 'id' | 'englishName'>,
  locale: string,
  translate?: TranslateFn,
): string => {
  const datasetName = getDatasetCountryName(country.id, locale);
  const fallback = datasetName ?? country.englishName;
  if (!translate) {
    return fallback;
  }

  const translated = translate(`country.${country.id}`, { defaultValue: fallback })?.trim();
  return translated?.length ? translated : fallback;
};

export const formatCountryDisplayLabel = (
  country: Pick<Country, 'id' | 'englishName'>,
  locale: string,
  translate?: TranslateFn,
): string => {
  const localizedName = resolveCountryLocalizedName(country, locale, translate);
  const englishName = country.englishName.trim();
  const isSameName = localizedName.localeCompare(englishName, undefined, { sensitivity: 'base' }) === 0;
  return isSameName ? englishName : `${localizedName} — ${englishName}`;
};

export const buildCountrySelectOption = (
  country: Pick<Country, 'id' | 'englishName'>,
  locale: string,
  translate?: TranslateFn,
) => ({
  value: country.id,
  label: formatCountryDisplayLabel(country, locale, translate),
  emoji: flagEmoji(country.id),
});
